import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, Facebook, Instagram, Youtube, Mail, ArrowUp } from 'lucide-react';
import { siteConfig, navLinks } from '../data/mock';
import { toast } from 'sonner';

// Import API URL from utility
import { API_URL } from '../lib/api';

const Footer = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNavClick = (link) => {
    if (link.isPage) {
      navigate(link.href);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      const element = document.querySelector(link.href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const openSocialLink = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setIsSubscribing(true);
    try {
      const response = await fetch(`${API_URL}/api/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, first_name: firstName || null })
      });
      
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || 'Successfully subscribed!');
        setEmail('');
        setFirstName('');
      } else {
        toast.error('Failed to subscribe. Please try again.');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error('Failed to subscribe. Please try again.');
    }
    setIsSubscribing(false);
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-900 text-white relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-px bg-gradient-to-r from-transparent via-coral-500/50 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand */}
          <div className="lg:col-span-1">
            <a href="#home" onClick={(e) => { e.preventDefault(); scrollToTop(); }} className="flex items-center gap-3 mb-6 group">
              <img
                src={siteConfig.logo}
                alt={siteConfig.name}
                className="h-16 w-16 object-contain bg-white/10 rounded-full p-1 transition-transform group-hover:scale-105"
              />
              <div>
                <h3 className="font-serif font-bold text-lg">{siteConfig.name}</h3>
                <p className="text-coral-400 text-sm">{siteConfig.tagline}</p>
              </div>
            </a>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              Equipping children and families with biblically sound Christian resources that nurture faith and spiritual growth.
            </p>
            
            {/* Social Links */}
            <div className="flex gap-3">
              <a 
                href={siteConfig.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-coral-500 transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href={siteConfig.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-coral-500 transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href={siteConfig.social.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-coral-500 transition-colors"
                aria-label="YouTube"
              >
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => handleNavClick(link)}
                    className="text-slate-400 hover:text-coral-400 transition-colors text-sm text-left"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="font-bold text-lg mb-6">Resources</h4>
            <ul className="space-y-3">
              <li><Link to="/resources" className="text-slate-400 hover:text-coral-400 transition-colors text-sm">Children's Books</Link></li>
              <li><Link to="/resources" className="text-slate-400 hover:text-coral-400 transition-colors text-sm">Teaching Tools</Link></li>
              <li><Link to="/resources" className="text-slate-400 hover:text-coral-400 transition-colors text-sm">Digital Platforms</Link></li>
              <li><Link to="/resources" className="text-slate-400 hover:text-coral-400 transition-colors text-sm">Parent Support</Link></li>
              <li><Link to="/contact" className="text-slate-400 hover:text-coral-400 transition-colors text-sm">Request Resources</Link></li>
            </ul>
            
            {/* Partner App */}
            <div className="mt-6 p-4 bg-white/5 rounded-xl border border-white/10">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">🔢</span>
                <span className="font-bold text-sm">NumberBay</span>
                <span className="bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">BETA</span>
              </div>
              <p className="text-slate-400 text-xs mb-3">
                A fun maths app for primary school children. Help shape its development — try it and share your feedback!
              </p>
              <a
                href="https://numberbay.lovable.app"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 font-medium transition-colors"
              >
                Try Early Access →
              </a>
            </div>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-bold text-lg mb-6">Stay Connected</h4>
            <p className="text-slate-400 text-sm mb-4">
              Subscribe to receive updates on new resources and ministry news.
            </p>
            <form className="space-y-3" onSubmit={handleSubscribe}>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Your first name"
                className="w-full bg-white/10 border border-white/10 rounded-xl px-4 py-3 text-sm placeholder:text-slate-500 focus:outline-none focus:border-coral-500 transition-colors"
              />
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full bg-white/10 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-sm placeholder:text-slate-500 focus:outline-none focus:border-coral-500 transition-colors"
                />
              </div>
              <button
                type="submit"
                disabled={isSubscribing}
                className="w-full bg-coral-500 hover:bg-coral-600 disabled:opacity-50 text-white rounded-xl py-3 text-sm font-semibold transition-colors"
              >
                {isSubscribing ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
          </div>
        </div>

        {/* Contact Info */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 text-center sm:text-left">
            <div>
              <h5 className="text-coral-400 font-semibold mb-2">Registered Charity</h5>
              <p className="text-slate-300">Charity Number: <span className="font-mono">{siteConfig.charityNumber}</span></p>
              <p className="text-slate-400 text-sm">Registered in England & Wales</p>
            </div>
            <div>
              <h5 className="text-coral-400 font-semibold mb-2">Contact Us</h5>
              <p className="text-slate-300">{siteConfig.phone}</p>
              <p className="text-slate-400 text-sm">{siteConfig.email}</p>
            </div>
            <div className="sm:col-span-2 lg:col-span-1">
              <h5 className="text-coral-400 font-semibold mb-2">Location</h5>
              <p className="text-slate-300">{siteConfig.address}</p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 pt-6 border-t border-white/10">
          <div className="flex flex-wrap justify-center gap-4 mb-4 text-xs">
            <Link to="/policy/privacy-policy" className="text-slate-400 hover:text-coral-400 transition-colors">Privacy Policy</Link>
            <Link to="/policy/cookie-policy" className="text-slate-400 hover:text-coral-400 transition-colors">Cookie Policy</Link>
            <Link to="/policy/terms-and-conditions" className="text-slate-400 hover:text-coral-400 transition-colors">Terms & Conditions</Link>
            <Link to="/policy/refund-and-returns" className="text-slate-400 hover:text-coral-400 transition-colors">Refund & Returns</Link>
            <Link to="/policy/shipping-and-delivery" className="text-slate-400 hover:text-coral-400 transition-colors">Shipping & Delivery</Link>
            <Link to="/my-data" className="text-slate-400 hover:text-coral-400 transition-colors">My Data & Privacy</Link>
          </div>
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <span>&copy; {currentYear} {siteConfig.name}.</span>
              <span className="flex items-center gap-1">
                Made with <Heart className="w-4 h-4 text-coral-500" fill="currentColor" /> for children everywhere.
              </span>
            </div>
            
            <div className="flex items-center gap-6">
              <button
                onClick={scrollToTop}
                className="w-10 h-10 bg-coral-500 hover:bg-coral-600 rounded-lg flex items-center justify-center transition-colors"
                aria-label="Scroll to top"
              >
                <ArrowUp className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
